import { SCENE_SIZE } from '@/editor/constant/scene';
import SublineModel from '@/editor/entities/2d/sublineModel';
import { pixelRatio2d } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Line2d } from '@/editor/math/Line2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { IRotatedBox } from '@/editor/types';
import { globalStores } from '@/editor/stores';

interface IArrayAbsorbResult {
  absorbPoint: IPoint2d;
  absorbSubLines: { start: IPoint2d; end: IPoint2d }[];
}

export class ArrayAbsorbManager {
  private _sublineModel = new SublineModel();

  constructor() {}

  calculateAbsorbPointThenRender(rawPoint: IPoint2d, box: IRotatedBox) {
    this.clearModel();

    const absorbResult = this.calculateOnly(rawPoint, box);
    if (!absorbResult) return rawPoint;

    this.updateModel(absorbResult);

    return absorbResult.absorbPoint;
  }

  calculateOnly(rawPoint: IPoint2d, box: IRotatedBox) {
    return this.calculateOrthogonalAbsorb(rawPoint, box);
  }

  private calculateOrthogonalAbsorb(rawPoint: IPoint2d, box: IRotatedBox): IArrayAbsorbResult | undefined {
    const xDirection = Point2dUtils.rotateDirection({ x: 1, y: 0 }, box.rotation);
    const yDirection = Point2dUtils.rotateDirection({ x: 0, y: 1 }, box.rotation);
    const axisInfos = [
      {
        direction: xDirection,
        line: new Line2d({
          parametric: {
            point: box.center,
            direction: xDirection
          }
        })
      },
      {
        direction: yDirection,
        line: new Line2d({
          parametric: {
            point: box.center,
            direction: yDirection
          }
        })
      }
    ];

    let minDistance = Infinity;
    let absorbResult: IArrayAbsorbResult | undefined;
    const axisRayLength = SCENE_SIZE * pixelRatio2d;
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;

    axisInfos.forEach(({ direction, line }) => {
      const footPoint = line.pointProjectOnLine(rawPoint);
      const distance = Point2dUtils.distanceTo(footPoint, rawPoint);
      if (distance > dynamicAbsorbDistance || distance >= minDistance) return;

      minDistance = distance;
      absorbResult = {
        absorbPoint: footPoint,
        absorbSubLines: [
          {
            start: footPoint,
            end: Point2dUtils.addDistance(footPoint, axisRayLength, direction)
          },
          {
            start: footPoint,
            end: Point2dUtils.addDistance(footPoint, -axisRayLength, direction)
          }
        ]
      };
    });

    return absorbResult;
  }

  private updateModel(absorbResult: IArrayAbsorbResult) {
    this._sublineModel.update(absorbResult.absorbSubLines);
  }

  clearModel() {
    this._sublineModel.clear();
  }

  dispose() {
    this._sublineModel.dispose();
  }
}
